import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import PortalHealthCard from '../../components/PortalHealthCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { POSITION_OPTIONS } from '../../lib/positions';
import {
  ArrowLeft, User, Clock, Calendar, MapPin, CheckCircle, XCircle, Image as ImageIcon,
  ArrowUpRight, ArrowDownRight, Edit2, Save, X, Download, FileText, Trash2, KeyRound,
  ShieldCheck, ShieldOff, GraduationCap, Mail, Hash, Briefcase, ArrowLeftRight, Star,
  CreditCard, MoreVertical, AlertTriangle, Activity, BarChart3, LogIn, LogOut, Check,
} from 'lucide-react';
import { format, parseISO, subDays, differenceInMinutes, startOfWeek, startOfMonth, startOfYear, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { formatUK } from '../../lib/ukTime';
import ComplianceDocuments from '../../components/ComplianceDocuments';
import EmploymentContract from '../../components/EmploymentContract';
import DayPdfButton from '../../components/DayPdfButton';
import AddShiftForEmployee from '../../components/AddShiftForEmployee';
import EmployeeAuditTimeline from '../../components/EmployeeAuditTimeline';
import AdminOnboardingPanel from '../../components/AdminOnboardingPanel';
import { ClockPunchDetail } from '../../components/ClockPunchDetail';
import ManualClockDialog from '../../components/ManualClockDialog';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { AppActivityPdfButton } from '../../components/AppActivityPdfButton';
import WorkHoursHistory from '../../components/WorkHoursHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin role options — informational for now; permissions are identical for
// every department until per-department permissions are wired.
const ADMIN_DEPARTMENTS = [
  'Secondary Admin', 'Operations', 'Control Room', 'Payroll',
  'HR & Compliance', 'Recruitment & Onboarding', 'Client Management', 'Quality & Audit',
];

export default function EmployeeDetailPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);

  const [employee, setEmployee] = useState(null);
  const { user: currentUser } = useAuth();
  const isMainAdmin = currentUser?.role === 'admin';
  const [roleDialog, setRoleDialog] = useState({ open: false, department: null, revert: false });
  const [clockHistory, setClockHistory] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [academicBreaks, setAcademicBreaks] = useState([]);
  const [policies, setPolicies] = useState([]); // each: {policy, users}
  const [procedures, setProcedures] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [qrCards, setQrCards] = useState([]);
  const [publicFeedback, setPublicFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [deleting, setDeleting] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, sendEmail: false });
  const [reportStartDate, setReportStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [reportEndDate, setReportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [downloading, setDownloading] = useState(false);
  const [updatingShiftId, setUpdatingShiftId] = useState(null);
  const [manualClockDialog, setManualClockDialog] = useState({ open: false, action: undefined });

  // Keep URL ?tab in sync
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'overview') params.delete('tab');
    else params.set('tab', tab);
    setSearchParams(params, { replace: true });
  }, [tab]);

  useEffect(() => { fetchData(); }, [employeeId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, supRes, historyRes, shiftsRes, breaksRes,
             policiesRes, proceduresRes, swapsRes, cardsRes, feedbackRes,
             profileRes] = await Promise.all([
        axios.get(`${API}/admin/employees`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/supervisors`).catch(() => ({ data: [] })),
        axios.get(`${API}/clock/history`, { params: { employee_id: employeeId, limit: 200 } }).catch(() => ({ data: [] })),
        axios.get(`${API}/shifts`, { params: { employee_id: employeeId } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users/${employeeId}/academic-breaks`).catch(() => ({ data: { breaks: [] } })),
        axios.get(`${API}/policies`).catch(() => ({ data: [] })),
        axios.get(`${API}/procedures`).catch(() => ({ data: [] })),
        Promise.resolve({ data: [] }), // shift-swap module removed 2026-02
        axios.get(`${API}/admin/staff-qr-cards`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/public-feedback`).catch(() => ({ data: { items: [] } })),
        // Dedicated profile fetch — the LIST endpoint runs results through
        // UserResponse(extra='ignore') which strips the extended
        // application fields (first_name, city, postcode…). The profile
        // endpoint returns the full _profile_view shape we need to drive
        // ApplicationDetailsCard and prevent the save→reload data-loss bug.
        axios.get(`${API}/admin/employees/${employeeId}/profile`).catch(() => ({ data: null })),
      ]);

      const allTeam = [...(empRes.data || []), ...(supRes.data || [])];
      const emp = allTeam.find((e) => e.id === employeeId);
      // Merge the full profile shape into the list-derived row so the page
      // has every extended field even though the list trims them.
      setEmployee(emp ? { ...emp, ...(profileRes.data || {}) } : null);
      setClockHistory(historyRes.data || []);
      setShifts(shiftsRes.data || []);
      setAcademicBreaks(breaksRes.data?.breaks || []);

      // Per-policy ack lookups for this user — uses admin-only status endpoint.
      // Limited concurrent loads for politeness.
      const allPolicies = policiesRes.data || [];
      const policyStatuses = await Promise.all(
        allPolicies.map(async (p) => {
          try {
            const res = await axios.get(`${API}/policies/${p.id}/status`);
            const userRow = (res.data?.users || []).find((u) => u.user_id === employeeId);
            return { id: p.id, title: p.title, acknowledged: !!userRow?.acknowledged, acknowledged_at: userRow?.acknowledged_at || null };
          } catch { return { id: p.id, title: p.title, acknowledged: false, acknowledged_at: null }; }
        })
      );
      setPolicies(policyStatuses);

      // Procedures: rely on `/procedures` (per-current-user) — for admin viewing other users
      // we cannot read per-user ack via current endpoints. So show titles only.
      const allProcedures = procedursesToList(proceduresRes.data || []);
      setProcedures(allProcedures);

      setSwaps((swapsRes.data || []).filter((s) =>
        s.requester_id === employeeId || s.swap_with_id === employeeId
      ));

      setQrCards((cardsRes.data || []).filter((c) => c.current_employee_id === employeeId));

      setPublicFeedback(((feedbackRes.data?.items) || []).filter(
        (f) => f.attributed_employee_id === employeeId
      ));
    } catch (error) {
      console.error('Failed to fetch employee detail:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Derived data ----------
  const stats = useMemo(() => {
    // Pair clock events to compute total minutes
    const ev = [...clockHistory].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    let totalMin = 0;
    let weekMin = 0; let monthMin = 0; let yearMin = 0;
    const now = new Date();
    const wkStart = startOfWeek(now, { weekStartsOn: 1 });
    const moStart = startOfMonth(now);
    const yrStart = startOfYear(now);
    let manualIns = 0; let autoOuts = 0; let editedEvents = 0;
    const ins = ev.filter((e) => e.event_type === 'clock_in');
    const outs = ev.filter((e) => e.event_type === 'clock_out');
    const usedOuts = new Set();
    for (const ci of ins) {
      if (ci.allocated_by || ci.manual_clock_in) manualIns += 1;
      if (ci.edited_by_admin) editedEvents += 1;
      const co = outs.find((o) => !usedOuts.has(o.id) && o.timestamp > ci.timestamp);
      if (co) {
        usedOuts.add(co.id);
        if (co.auto_clock_out) autoOuts += 1;
        if (co.edited_by_admin) editedEvents += 1;
        try {
          const minutes = differenceInMinutes(parseISO(co.timestamp), parseISO(ci.timestamp));
          totalMin += minutes;
          const t = parseISO(ci.timestamp);
          if (t >= wkStart) weekMin += minutes;
          if (t >= moStart) monthMin += minutes;
          if (t >= yrStart) yearMin += minutes;
        } catch { /* skip */ }
      }
    }
    const events = new Set(shifts.filter((s) => s.event_id || s.event_group_id).map((s) => s.event_id || s.event_group_id));
    return {
      totalShifts: shifts.length,
      totalHours: (totalMin / 60).toFixed(1),
      weekHours: (weekMin / 60).toFixed(1),
      monthHours: (monthMin / 60).toFixed(1),
      yearHours: (yearMin / 60).toFixed(1),
      manualIns, autoOuts, editedEvents,
      eventsAttended: events.size,
    };
  }, [clockHistory, shifts]);

  const pairedEvents = useMemo(() => pairClockEvents(clockHistory), [clockHistory]);

  // ---------- Actions (preserved from old page) ----------
  const viewPhoto = async (eventId) => {
    setPhotoLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clock/photo/${eventId}`);
      if (res.data.photo_url && res.data.photo_url !== 'has_photo') {
        setSelectedPhoto(res.data.photo_url);
      } else { toast.error('No photo available'); }
    } catch { toast.error('Failed to load photo'); }
    finally { setPhotoLoading(false); }
  };

  const handleUpdateShiftStatus = async (shiftId, newStatus) => {
    setUpdatingShiftId(shiftId);
    try {
      await axios.put(`${API}/shifts/${shiftId}`, { status: newStatus });
      toast.success(`Shift marked as ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update shift'); }
    finally { setUpdatingShiftId(null); }
  };

  const handleToggleStatus = () => {
    setStatusModal({ open: true, sendEmail: false });
  };

  const handleChangeAccountRole = async () => {
    const payload = roleDialog.revert
      ? { role: 'employee' }
      : { role: 'supervisor', admin_department: roleDialog.department };
    try {
      const res = await axios.put(`${API}/admin/users/${employeeId}/account-role`, payload);
      toast.success(res.data.message);
      setRoleDialog({ open: false, department: null, revert: false });
      setEmployee((prev) => (prev ? { ...prev, role: res.data.role, admin_department: res.data.admin_department } : prev));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const confirmToggleStatus = async () => {
    try {
      const res = await axios.put(`${API}/admin/employees/${employeeId}/status`, { send_email: statusModal.sendEmail });
      toast.success(res.data.message);
      setStatusModal({ open: false, sendEmail: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleToggleAutoApprove = async () => {
    try {
      const next = !employee.auto_approve_claims;
      await axios.put(`${API}/admin/employees/${employeeId}/auto-approve`, { enabled: next });
      toast.success(next ? 'Auto-approve enabled — future claims skip the queue' : 'Auto-approve disabled — future claims need approval');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle auto-approve');
    }
  };


  const handleResetPassword = async () => {
    try {
      const res = await axios.post(`${API}/admin/employees/${employeeId}/reset-password`);
      toast.success(`Temp password: ${res.data.temp_password}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleDownloadDocuments = async (mode) => {
    // mode: 'zip' | 'pdf'
    const endpoint = mode === 'pdf'
      ? `documents/download-combined-pdf`
      : `documents/download-zip`;
    const fallbackName = `documents.${mode === 'pdf' ? 'pdf' : 'zip'}`;
    try {
      toast.loading('Preparing download…', { id: 'doc-dl' });
      const res = await axios.get(
        `${API}/admin/employees/${employeeId}/${endpoint}`,
        { responseType: 'blob' }
      );
      // Try to read filename from Content-Disposition
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const fname = match ? match[1] : fallbackName;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('Download started', { id: 'doc-dl' });
    } catch (err) {
      const detail = err.response?.status === 404
        ? 'No documents to download'
        : (err.response?.data?.detail || 'Download failed');
      toast.error(detail, { id: 'doc-dl' });
    }
  };

  const handleEditClick = (event) => {
    setEditDate(formatUK(event.timestamp, 'yyyy-MM-dd'));
    setEditTime(formatUK(event.timestamp, 'HH:mm'));
    setEditingEvent(event);
  };

  const handleSaveEdit = async () => {
    if (!editDate || !editTime) { toast.error('Pick a date and time'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/clock/edit/${editingEvent.id}`, {
        new_timestamp: `${editDate}T${editTime}:00`,
      });
      toast.success('Clock time updated');
      setEditingEvent(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDeleteEvent = async () => {
    if (!deleteDialog.event) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/clock/${deleteDialog.event.id}`);
      toast.success('Clock event deleted');
      setDeleteDialog({ open: false, event: null });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const fShifts = shifts.filter((s) => s.date >= reportStartDate && s.date <= reportEndDate);
      const fHistory = clockHistory.filter((e) => {
        const d = e.timestamp.substring(0, 10);
        return d >= reportStartDate && d <= reportEndDate;
      });
      let csv = `Employee Report: ${employee?.name}\nEmail: ${employee?.email}\nPeriod: ${reportStartDate} → ${reportEndDate}\n\nSHIFTS\nDate,Start,End,Site,Status\n`;
      for (const s of fShifts) csv += `"${s.date}","${s.start_time || ''}","${s.end_time || ''}","${s.site_name || '-'}","${s.status}"\n`;
      csv += `\nCLOCK EVENTS\nDate,Time,Type\n`;
      for (const e of fHistory) {
        const d = format(parseISO(e.timestamp), 'yyyy-MM-dd');
        const t = format(parseISO(e.timestamp), 'HH:mm:ss');
        csv += `"${d}","${t}","${e.event_type === 'clock_in' ? 'In' : 'Out'}"\n`;
      }
      csv += `\nSUMMARY\nTotal Shifts,${fShifts.length}\nTotal Hours,${stats.totalHours}\n`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(employee?.name || 'employee').replace(/\s+/g, '_')}_report_${reportStartDate}_to_${reportEndDate}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
      setReportDialog(false);
    } catch { toast.error('Failed to generate report'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!employee) {
    return (
      <AdminLayout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Employee not found</p>
          <Button onClick={() => navigate('/admin/employees')} className="mt-4">Back</Button>
        </div>
      </AdminLayout>
    );
  }

  const isActive = employee.status === 'active';
  const fullName = employee.name || '';
  const initials = fullName.split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join('');

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-5 min-h-screen">
        {/* Header */}
        <div className="border-b border-border pb-4 sm:pb-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-12 h-12 sm:w-14 sm:h-14 rounded-sm flex items-center justify-center text-base font-medium flex-shrink-0',
              employee.is_on_shift ? 'bg-green-400/10 text-green-400'
                : employee.role === 'supervisor' ? 'bg-blue-400/10 text-blue-400'
                : 'bg-accent text-muted-foreground'
            )}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-2xl font-barlow tracking-widest text-foreground truncate" data-testid="employee-name">
                  {fullName}
                </h1>
                <ComplianceIndicator userId={employee.id} size="md" />
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{employee.email}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge className={cn('text-[10px] uppercase tracking-wider',
                  employee.role === 'supervisor' ? 'bg-blue-600/10 text-blue-400 border-blue-900' : 'bg-muted text-muted-foreground border-border')}>
                  {employee.role === 'supervisor' ? <><ShieldCheck className="w-3 h-3 mr-1" /> Supervisor</> : 'Employee'}
                </Badge>
                {employee.admin_department && (
                  <Badge
                    className="bg-blue-600/10 text-blue-400 border-blue-900 text-[10px] uppercase tracking-wider"
                    data-testid="admin-department-badge"
                  >
                    {employee.role === 'supervisor' ? 'Admin' : 'Dept'} — {employee.admin_department}
                  </Badge>
                )}
                {employee.is_on_shift ? (
                  <Badge className="status-active text-[10px] uppercase tracking-wider">
                    <CheckCircle className="w-3 h-3 mr-1" /> On Shift
                  </Badge>
                ) : isActive ? (
                  <Badge className="bg-muted text-muted-foreground border-border text-[10px] uppercase tracking-wider">
                    <XCircle className="w-3 h-3 mr-1" /> Off Duty
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/15 text-red-500 border-red-500/40 text-[10px] uppercase tracking-wider">
                    <ShieldOff className="w-3 h-3 mr-1" /> Suspended
                  </Badge>
                )}
                {employee.auto_suspended && employee.status !== 'active' && (
                  <Badge
                    className="bg-amber-500/15 text-amber-500 border-amber-500/40 text-[10px] uppercase tracking-wider"
                    data-testid="auto-suspended-badge"
                    title="Suspended automatically after 30 days of inactivity"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> Inactivity
                  </Badge>
                )}
                {employee.onboarding_required ? (
                  <Badge
                    className="bg-orange-500/15 text-orange-500 border-orange-500/40 text-[10px] uppercase tracking-wider"
                    data-testid="onboarding-status-badge"
                    title="Employee has not yet completed onboarding — access to operational sections is locked"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> Onboarding: Incomplete
                  </Badge>
                ) : (
                  employee.onboarding_completed_at ? (
                    <Badge
                      className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px] uppercase tracking-wider"
                      data-testid="onboarding-status-badge"
                      title={`Onboarding completed ${employee.onboarding_completed_at}`}
                    >
                      Onboarding: Complete
                    </Badge>
                  ) : null
                )}
                {employee.email_verified ? (
                  <Badge
                    className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px] uppercase tracking-wider"
                    data-testid="email-verified-badge"
                    title={employee.email_verified_at ? `Email verified ${employee.email_verified_at}` : 'Email verified'}
                  >
                    Email: Verified
                  </Badge>
                ) : (
                  employee.email_verification_required ? (
                    <Badge
                      className="bg-amber-500/15 text-amber-500 border-amber-500/40 text-[10px] uppercase tracking-wider"
                      data-testid="email-verified-badge"
                      title="Employee has not verified their email address yet"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" /> Email: Not Verified
                    </Badge>
                  ) : null
                )}
              </div>
              <p
                className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1"
                data-testid="employee-last-login"
              >
                <Clock className="w-3 h-3" strokeWidth={1.5} />
                <span className="uppercase tracking-wider">Last login:</span>
                <span className="font-mono text-foreground/80">
                  {employee.last_login_at
                    ? (() => {
                        try { return format(parseISO(employee.last_login_at), 'dd MMM yyyy, HH:mm'); }
                        catch { return employee.last_login_at; }
                      })()
                    : 'Never'}
                </span>
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="border-border rounded-sm flex-shrink-0" data-testid="admin-actions-menu">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                <DropdownMenuItem onClick={() => setReportDialog(true)} data-testid="download-report-action">
                  <Download className="w-4 h-4 mr-2" /> Download Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadDocuments('zip')} data-testid="download-docs-zip-action">
                  <Download className="w-4 h-4 mr-2 text-blue-400" /> Download Documents (ZIP)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadDocuments('pdf')} data-testid="download-docs-pdf-action">
                  <Download className="w-4 h-4 mr-2 text-blue-400" /> Download Documents (combined PDF)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleResetPassword} data-testid="reset-password-action">
                  <KeyRound className="w-4 h-4 mr-2 text-amber-400" /> Reset Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {employee.is_on_shift ? (
                  <DropdownMenuItem
                    onClick={() => setManualClockDialog({ open: true, action: 'out' })}
                    data-testid="manual-clock-out-action"
                  >
                    <LogOut className="w-4 h-4 mr-2 text-amber-400" /> Manual Clock Out
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setManualClockDialog({ open: true, action: 'in' })}
                    data-testid="manual-clock-in-action"
                  >
                    <LogIn className="w-4 h-4 mr-2 text-emerald-400" /> Manual Clock In
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleToggleAutoApprove} data-testid="toggle-auto-approve-action">
                  {employee.auto_approve_claims ? (
                    <><ShieldOff className="w-4 h-4 mr-2 text-amber-400" /> Disable auto-approve claims</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-2 text-emerald-400" /> Enable auto-approve claims</>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleToggleStatus} data-testid="toggle-status-action">
                  {isActive ? <><ShieldOff className="w-4 h-4 mr-2 text-red-400" /> Suspend</> : <><ShieldCheck className="w-4 h-4 mr-2 text-green-400" /> Reactivate</>}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {isMainAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="detail-assign-admin-role-btn"
                  className="w-full sm:w-auto rounded-sm text-xs uppercase tracking-wider border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Assign Admin Role
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border rounded-sm min-w-[240px]">
                {ADMIN_DEPARTMENTS.map((dept) => (
                  <DropdownMenuItem
                    key={dept}
                    onClick={() => setRoleDialog({ open: true, department: dept, revert: false })}
                    data-testid={`assign-admin-${dept.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="text-sm"
                  >
                    {employee.admin_department === dept
                      ? <Check className="w-4 h-4 mr-2 text-emerald-400" />
                      : <span className="w-4 h-4 mr-2 inline-block" />}
                    {dept}
                  </DropdownMenuItem>
                ))}
                {(employee.role === 'supervisor' || employee.admin_department) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setRoleDialog({ open: true, department: null, revert: true })}
                      data-testid="assign-admin-revert"
                      className="text-sm text-red-400 focus:text-red-400"
                    >
                      <ShieldOff className="w-4 h-4 mr-2" /> Revert to Employee
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Promote / demote / department-change confirmation */}
        <AlertDialog open={roleDialog.open} onOpenChange={(v) => setRoleDialog((p) => ({ ...p, open: v }))}>
          <AlertDialogContent className="bg-card border-border rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider uppercase">
                {roleDialog.revert ? 'Revert to Employee' : `Assign Admin Role — ${roleDialog.department || ''}`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {roleDialog.revert
                  ? `${fullName} will lose admin access and return to the employee portal at their next login or page refresh. Same account, same password.`
                  : roleDialog.department === 'Operations'
                    ? `${fullName} keeps their employee portal (MY AREA unchanged) and gets the OPERATIONS menu: Event & Retail Calendars, Events Manager and Staffing Requests. No clock history, no payroll, deletions require your approval.${employee.role === 'supervisor' ? ' ⚠ NOTE: they currently have the Secondary Admin portal — they will LOSE it and move to the employee portal with the Operations menu.' : ''}`
                    : employee.role === 'supervisor'
                      ? `${fullName}'s assigned admin role changes to "${roleDialog.department}". Portal access and permissions stay exactly the same for now.`
                      : `${fullName} will get the Secondary Admin portal — full admin menu, but no deletions and no emails — with the assigned role "${roleDialog.department}". The switch happens at their next login or page refresh. Same account, same password.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleChangeAccountRole}
                data-testid="detail-confirm-change-role-btn"
                className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
              >
                {roleDialog.revert ? 'Revert' : 'Assign'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-4" data-testid="detail-tabs">
          <div className="overflow-x-auto scrollbar-thin">
            <TabsList className="bg-card border border-border rounded-sm inline-flex flex-nowrap">
              <TabsTrigger value="overview"      data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="schedule"      data-testid="tab-schedule">Schedule</TabsTrigger>
              <TabsTrigger value="clock-history" data-testid="tab-clock-history">Clock History</TabsTrigger>
              <TabsTrigger value="documents"     data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="qr"            data-testid="tab-qr">QR Feedback</TabsTrigger>
              <TabsTrigger value="swaps"         data-testid="tab-swaps">Swap Requests</TabsTrigger>
              <TabsTrigger value="stats"         data-testid="tab-stats">Statistics</TabsTrigger>
              {isMainAdmin && <TabsTrigger value="work-hours" data-testid="tab-work-hours">Work Hours History</TabsTrigger>}
              <TabsTrigger value="audit"         data-testid="tab-audit">Audit</TabsTrigger>
            </TabsList>
          </div>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4">
            <PortalHealthCard employeeId={employeeId} />
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" strokeWidth={1.5} /> Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <InfoRow icon={User}      label="Full Name"     value={fullName} />
                <InfoRow icon={Mail}      label="Email"         value={employee.email} />
                <InfoRow icon={Briefcase} label="Phone"         value={employee.phone || '—'} />
                <InfoRow icon={Hash}      label="Employee Code" value={employee.employee_code || '—'} mono />
                <InfoRow icon={ShieldCheck} label="Role"        value={employee.role === 'supervisor' ? 'Supervisor' : 'Employee'} />
                <PositionEditor
                  employeeId={employeeId}
                  value={employee.position || ''}
                  onSaved={(next) => setEmployee((prev) => prev ? { ...prev, position: next } : prev)}
                />
              </CardContent>
            </Card>

            <ApplicationDetailsCard
              employee={employee}
              employeeId={employeeId}
              onSaved={(updated) => setEmployee((prev) => prev ? { ...prev, ...updated } : prev)}
            />

            {isMainAdmin && <BankDetailsCard employeeId={employeeId} />}

            {isMainAdmin && <NationalInsuranceCard employeeId={employeeId} />}

            <Card className="bg-card border-border rounded-sm" data-testid="app-activity-export-card">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-barlow tracking-wider uppercase">App Activity Report</p>
                  <p className="text-[11px] text-muted-foreground">
                    Monthly PDF of logins + clock in/out — proof for payroll disputes.
                  </p>
                </div>
                <AppActivityPdfButton userId={employeeId} userName={employee?.name} />
              </CardContent>
            </Card>

            {/* Onboarding & Compliance panel — status, versioning, admin
                actions (Complete / Request Re-Ack) + audit log. */}
            <AdminOnboardingPanel
              employeeId={employeeId}
              onUpdated={fetchData}
            />

            {academicBreaks.length > 0 && (
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" /> Academic Breaks ({academicBreaks.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {academicBreaks.map((b, i) => <BreakCard key={b.id || i} brk={b} index={i} />)}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SCHEDULE */}
          <TabsContent value="schedule" className="space-y-4">
            <div className="flex justify-end">
              <AddShiftForEmployee employeeId={employeeId} onCreated={fetchData} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatTile label="This Week"  value={`${stats.weekHours}h`}  icon={Clock} tone="primary" testId="stat-week-hours" />
              <StatTile label="This Month" value={`${stats.monthHours}h`} icon={Clock} tone="blue" testId="stat-month-hours" />
              <StatTile label="This Year"  value={`${stats.yearHours}h`}  icon={Clock} tone="green" testId="stat-year-hours" />
            </div>

            <ScheduleCalendar
              shifts={shifts}
              updatingShiftId={updatingShiftId}
              onUpdateStatus={handleUpdateShiftStatus}
              clockHistory={clockHistory}
              onViewPhoto={viewPhoto}
            />
          </TabsContent>

          {/* CLOCK HISTORY */}
          <TabsContent value="clock-history" className="space-y-4">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" /> Clock In / Out ({pairedEvents.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {pairedEvents.length === 0 ? (
                  <EmptyState icon={Clock} title="No clock history" />
                ) : (
                  <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
                    {pairedEvents.map((p, i) => (
                      <ClockPairRow
                        key={i} pair={p}
                        employeeId={employeeId}
                        employeeName={fullName}
                        onEdit={handleEditClick}
                        onDelete={(ev) => setDeleteDialog({ open: true, event: ev })}
                        onViewPhoto={viewPhoto}
                        photoLoading={photoLoading}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents" className="space-y-4">
            <EmploymentContract mode="admin" employeeId={employeeId} />
            <ComplianceDocuments mode="admin" employeeId={employeeId} />

            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Policies ({policies.filter((p) => p.acknowledged).length}/{policies.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {policies.length === 0 ? (
                  <EmptyState icon={FileText} title="No policies defined" />
                ) : (
                  <div className="divide-y divide-border max-h-[40vh] overflow-y-auto">
                    {policies.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 sm:p-4 hover:bg-accent/30">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{p.title}</p>
                          {p.acknowledged_at && (
                            <p className="text-xs text-muted-foreground">
                              Acknowledged {format(parseISO(p.acknowledged_at), 'MMM d, yyyy h:mm a')}
                            </p>
                          )}
                        </div>
                        {p.acknowledged ? (
                          <Badge className="bg-green-500/15 text-green-500 border-green-500/40">
                            <CheckCircle className="w-3 h-3 mr-1" /> Acknowledged
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/15 text-red-500 border-red-500/40">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Missing
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {procedures.length > 0 && (
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" /> Procedures ({procedures.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border max-h-[30vh] overflow-y-auto">
                    {procedures.map((p) => (
                      <div key={p.id} className="p-3 sm:p-4 text-sm">
                        <p className="font-medium">{p.title}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {academicBreaks.length > 0 && (
              <Card className="bg-card border-border rounded-sm">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary" /> Academic Breaks ({academicBreaks.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {academicBreaks.map((b, i) => <BreakCard key={b.id || i} brk={b} index={i} />)}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* QR FEEDBACK */}
          <TabsContent value="qr" className="space-y-4">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" /> Assigned QR Cards ({qrCards.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {qrCards.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No QR card assigned. Manage assignments from Staff Feedback Cards.
                  </p>
                ) : (
                  qrCards.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-sm">
                      <div className="min-w-0">
                        <p className="text-sm font-mono font-bold">{c.card_code}</p>
                        {c.event_label && <p className="text-xs text-muted-foreground">{c.event_label}</p>}
                      </div>
                      <Badge className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-500 border-amber-500/40">
                        {c.feedback_count || 0} reviews
                      </Badge>
                    </div>
                  ))
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/feedback-cards')}
                  data-testid="manage-qr-cards-btn"
                  className="w-full border-border rounded-sm"
                >
                  Manage QR Card Assignments
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <Star className="w-4 h-4 text-primary" /> Public Feedback ({publicFeedback.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {publicFeedback.length === 0 ? (
                  <EmptyState icon={Star} title="No feedback yet" />
                ) : (
                  <div className="divide-y divide-border max-h-[40vh] overflow-y-auto">
                    {publicFeedback.map((f) => <FeedbackRow key={f.id} feedback={f} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SWAPS */}
          <TabsContent value="swaps" className="space-y-4">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <ArrowLeftRight className="w-4 h-4 text-primary" /> Swap Requests ({swaps.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {swaps.length === 0 ? (
                  <EmptyState icon={ArrowLeftRight} title="No swap requests" />
                ) : (
                  <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                    {swaps.map((s) => <SwapRow key={s.id} swap={s} currentId={employeeId} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGES — moved to the main menu (Admin → Messages). */}

          {/* STATISTICS */}
          <TabsContent value="stats" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatTile label="Total Shifts"      value={stats.totalShifts}  icon={Calendar} tone="primary" testId="stat-total-shifts" />
              <StatTile label="Total Hours"       value={`${stats.totalHours}h`} icon={Clock} tone="blue" testId="stat-total-hours" />
              <StatTile label="Events Attended"   value={stats.eventsAttended} icon={Briefcase} tone="amber" testId="stat-events-attended" />
              <StatTile label="Manual Clock-Ins"  value={stats.manualIns}    icon={AlertTriangle} tone="amber" testId="stat-manual-ins" />
              <StatTile label="Auto Clock-Outs"   value={stats.autoOuts}     icon={AlertTriangle} tone="red" testId="stat-auto-outs" />
            </div>
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" /> Hours Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">This Week</span><span className="font-mono">{stats.weekHours}h</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">This Month</span><span className="font-mono">{stats.monthHours}h</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">This Year</span><span className="font-mono">{stats.yearHours}h</span></div>
                <div className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">Edited Clock Events</span><span className="font-mono">{stats.editedEvents}</span></div>
              </CardContent>
            </Card>
          </TabsContent>

          {isMainAdmin && (
            <TabsContent value="work-hours" className="space-y-4">
              <WorkHoursHistory employeeId={employeeId} />
            </TabsContent>
          )}

          <TabsContent value="audit" className="space-y-4">
            <EmployeeAuditTimeline employeeId={employeeId} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <div className="max-w-2xl max-h-[80vh] relative">
            <img src={selectedPhoto} alt="Clock" className="max-w-full max-h-[80vh] object-contain rounded-sm" />
            <Button variant="destructive" size="icon" className="absolute top-2 right-2" onClick={() => setSelectedPhoto(null)}>
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editingEvent} onOpenChange={(open) => !open && setEditingEvent(null)}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase text-primary">Edit Clock Time</DialogTitle>
            <DialogDescription>
              {editingEvent && (
                <>Update {editingEvent.event_type === 'clock_in' ? 'clock in' : 'clock out'} for {fullName}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
              <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                className="bg-background border-border rounded-sm" data-testid="edit-date-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Time</Label>
              <Input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)}
                className="bg-background border-border rounded-sm" data-testid="edit-time-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEvent(null)} disabled={saving} className="border-border rounded-sm">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">Delete Clock Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEvent} disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status change confirmation modal (with optional email checkbox) */}
      <Dialog open={statusModal.open} onOpenChange={(o) => !o && setStatusModal({ open: false, sendEmail: false })}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="status-modal">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase text-primary">
              {isActive ? 'Suspend account?' : 'Reactivate account?'}
            </DialogTitle>
            <DialogDescription>
              {isActive
                ? 'The employee will no longer be able to sign in until reactivated.'
                : 'The employee will regain access to the app and their account.'}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={statusModal.sendEmail}
              onChange={(e) => setStatusModal({ ...statusModal, sendEmail: e.target.checked })}
              className="rounded-sm border-border"
              data-testid="status-send-email-checkbox"
            />
            <span className="text-sm">Send email notification to employee</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal({ open: false, sendEmail: false })} className="rounded-sm">Cancel</Button>
            <Button onClick={confirmToggleStatus} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="confirm-status-btn">
              {isActive ? 'Suspend' : 'Reactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Report dialog */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" /> Download Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3 grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">From</Label>
              <Input type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)}
                className="bg-background border-border rounded-sm" data-testid="report-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">To</Label>
              <Input type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)}
                className="bg-background border-border rounded-sm" data-testid="report-end-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialog(false)} className="border-border rounded-sm">Cancel</Button>
            <Button onClick={handleDownloadReport} disabled={downloading}
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm" data-testid="generate-report-btn">
              <Download className="w-4 h-4 mr-2" /> {downloading ? 'Generating...' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Clock In/Out (admin override) */}
      <ManualClockDialog
        open={manualClockDialog.open}
        onOpenChange={(open) => setManualClockDialog((prev) => ({ ...prev, open }))}
        employee={employee ? { id: employee.id, name: fullName, is_on_shift: !!employee.is_on_shift } : null}
        defaultAction={manualClockDialog.action}
        onSuccess={fetchData}
      />
    </AdminLayout>
  );
}

// ---------- Small sub-components ----------
// One-click copy for profile values — no manual text selection needed.
function CopyBtn({ value, label, className }) {
  const [copied, setCopied] = useState(false);
  const text = (value ?? '').toString().trim();
  if (!text || text === '—') return null;
  const doCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success(`${label || 'Value'} copied`);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error('Copy failed');
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      title={`Copy ${label || 'value'}`}
      className={cn('p-1 rounded-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0', className)}
      data-testid={`copy-btn-${(label || 'value').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2.5 p-2 rounded-sm bg-background/60 border border-border/60">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={cn('text-sm font-medium truncate', mono && 'font-mono')}>{value || '—'}</p>
      </div>
      <CopyBtn value={value} label={label} className="mt-1" />
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone, testId }) {
  const styles = {
    primary: 'text-primary bg-primary/10',
    blue: 'text-blue-500 bg-blue-500/10',
    green: 'text-green-500 bg-green-500/10',
    amber: 'text-amber-500 bg-amber-500/10',
    red: 'text-red-500 bg-red-500/10',
  };
  return (
    <Card className="bg-card border-border rounded-sm" data-testid={testId}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className={cn('p-1.5 sm:p-2 rounded-sm', styles[tone])}>
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <span className="font-mono text-xl sm:text-2xl font-medium">{value}</span>
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-2">{label}</p>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, title }) {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-50" />
      <p className="text-sm">{title}</p>
    </div>
  );
}

function BreakCard({ brk, index }) {
  let label = 'Past';
  let cls = 'bg-slate-500/20 text-slate-400 border-slate-500/40';
  try {
    const todayIso = formatUK(new Date(), 'yyyy-MM-dd');
    if (todayIso >= brk.start_date && todayIso <= brk.end_date) { label = 'On Break Now'; cls = 'bg-amber-500/20 text-amber-400 border-amber-500/40'; }
    else if (todayIso < brk.start_date) { label = 'Upcoming'; cls = 'bg-blue-500/20 text-blue-400 border-blue-500/40'; }
  } catch { /* ignore */ }
  return (
    <div className="p-3 bg-background border border-border rounded-sm space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Break #{index + 1}</span>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${cls}`}>{label}</span>
      </div>
      <p className="text-sm font-mono">
        {(() => { try { return format(parseISO(brk.start_date), 'dd MMM yyyy'); } catch { return brk.start_date; } })()}
        <span className="text-muted-foreground mx-1">→</span>
        {(() => { try { return format(parseISO(brk.end_date), 'dd MMM yyyy'); } catch { return brk.end_date; } })()}
      </p>
    </div>
  );
}

function ScheduleCalendar({ shifts, updatingShiftId, onUpdateStatus, clockHistory, onViewPhoto }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  }), [currentMonth]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const getShiftsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter((s) => s.date === dateStr);
  };

  const monthStr = format(currentMonth, 'yyyy-MM');
  const monthCount = shifts.filter((s) => (s.date || '').startsWith(monthStr)).length;
  const selectedShifts = selectedDate ? getShiftsForDate(selectedDate) : [];

  return (
    <>
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" /> {format(currentMonth, 'MMMM yyyy')}
              <span className="text-xs text-muted-foreground normal-case tracking-normal font-normal">({monthCount} shift{monthCount === 1 ? '' : 's'})</span>
            </CardTitle>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => { setCurrentMonth(subMonths(currentMonth, 1)); setSelectedDate(null); }} className="h-8 w-8" data-testid="schedule-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => { setCurrentMonth(addMonths(currentMonth, 1)); setSelectedDate(null); }} className="h-8 w-8" data-testid="schedule-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
              <div key={d} className="text-center text-xs text-muted-foreground py-1.5 font-medium">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: days[0].getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="h-12 sm:h-14" />
            ))}
            {days.map((day) => {
              const dayShifts = getShiftsForDate(day);
              const dayStr = format(day, 'yyyy-MM-dd');
              const isToday = dayStr === todayStr;
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              let cls = 'bg-muted/40 text-foreground';
              if (dayShifts.length > 0) {
                if (dayShifts.every((s) => s.status === 'completed')) cls = 'bg-red-500 text-white';
                else if (dayStr === todayStr) cls = 'bg-yellow-500 text-black';
                else if (dayStr > todayStr) cls = 'bg-green-600 text-white';
                else cls = 'bg-blue-500 text-white';
              }
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  data-testid={`schedule-day-${dayStr}`}
                  className={cn(
                    'h-12 sm:h-14 flex flex-col items-center justify-center rounded-sm transition-all text-xs font-medium gap-0.5 p-0.5',
                    cls,
                    isToday && 'ring-2 ring-yellow-400 ring-offset-1 ring-offset-card',
                    isSelected && 'outline outline-2 outline-primary',
                  )}
                >
                  <span className="text-sm leading-none">{format(day, 'd')}</span>
                  {dayShifts.length > 0 && (
                    <span className="text-[9px] leading-none px-1 py-0.5 rounded-sm bg-black/30 text-white whitespace-nowrap">
                      {dayShifts.length === 1
                        ? (dayShifts[0].start_time || 'TBC')
                        : `${dayShifts.length} shifts`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-600 inline-block" /> Upcoming</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-500 inline-block" /> Today</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500 inline-block" /> Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" /> Past (not completed)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-muted/40 border border-border inline-block" /> No shift</span>
          </div>
        </CardContent>
      </Card>

      {selectedDate && (
        <Card className="bg-card border-border rounded-sm" data-testid="schedule-day-detail">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase text-primary">
              {format(selectedDate, 'EEEE, MMM d yyyy')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {selectedShifts.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">No shifts on this day.</p>
            ) : (
              <div className="divide-y divide-border">
                {selectedShifts.map((shift) => (
                  <ShiftRow key={shift.id} shift={shift}
                    updating={updatingShiftId === shift.id}
                    onUpdateStatus={(s) => onUpdateStatus(shift.id, s)}
                    clockHistory={clockHistory}
                    onViewPhoto={onViewPhoto} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function ShiftRow({ shift, updating, onUpdateStatus, clockHistory, onViewPhoto }) {
  const { inEv, outEv } = useMemo(() => {
    const all = clockHistory || [];
    let evs = all.filter((e) => e.shift_id === shift.id);
    if (evs.length === 0) {
      evs = all.filter((e) => !e.shift_id && (e.timestamp || '').startsWith(shift.date));
    }
    const ins = evs.filter((e) => e.event_type === 'clock_in').sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    const outs = evs.filter((e) => e.event_type === 'clock_out').sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    return { inEv: ins[0] || null, outEv: outs[outs.length - 1] || null };
  }, [clockHistory, shift.id, shift.date]);

  return (
    <div className="p-3 sm:p-4 hover:bg-accent/30 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {(() => { try { return format(parseISO(shift.date), 'EEE, MMM d, yyyy'); } catch { return shift.date; } })()}
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={updating} data-testid={`shift-status-trigger-${shift.id}`}
              className="focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-sm">
              <Badge className={cn(
                'text-xs cursor-pointer hover:opacity-80',
                shift.status === 'completed' ? 'status-active' :
                shift.status === 'in_progress' ? 'bg-primary/20 text-primary border border-primary/30' :
                shift.status === 'missed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                'status-inactive'
              )}>
                {updating ? 'Saving...' : (shift.status || 'scheduled').replace('_', ' ')}
              </Badge>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
            {['scheduled', 'in_progress', 'completed', 'missed'].map((s) => (
              <DropdownMenuItem key={s} onClick={() => onUpdateStatus(s)} data-testid={`set-status-${s}-${shift.id}`}>
                {s.replace('_', ' ')}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-xs text-muted-foreground font-mono mt-1">
        {shift.start_time && shift.end_time ? `${shift.start_time} - ${shift.end_time}` : (
          <span className="text-amber-400 font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm">
            Times Pending
          </span>
        )}
      </p>
      {shift.site_name && (
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {shift.site_name}
        </p>
      )}

      {/* Clock in / out details */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <ClockPunchDetail kind="in" event={inEv} onViewPhoto={onViewPhoto} />
        <ClockPunchDetail kind="out" event={outEv} onViewPhoto={onViewPhoto} />
      </div>
    </div>
  );
}

function ClockPairRow({ pair, employeeId, employeeName, onEdit, onDelete, onViewPhoto, photoLoading }) {
  return (
    <div className="p-3 sm:p-4 hover:bg-accent/30 transition-colors">
      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
        {(() => { try { return format(parseISO(pair.date), 'EEE, MMM d, yyyy'); } catch { return pair.date; } })()}
        <DayPdfButton employeeId={employeeId} employeeName={employeeName} date={pair.date} />
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ClockSide kind="in"  event={pair.clockIn}  onEdit={onEdit} onDelete={onDelete} onViewPhoto={onViewPhoto} photoLoading={photoLoading} />
        <ClockSide kind="out" event={pair.clockOut} onEdit={onEdit} onDelete={onDelete} onViewPhoto={onViewPhoto} photoLoading={photoLoading} />
      </div>
      {(pair.clockIn?.late_reason || pair.clockOut?.late_reason) && (
        <div className="mt-2 space-y-1.5">
          {pair.clockIn?.late_reason && (
            <div className="flex items-start gap-2 text-xs bg-amber-500/10 border-l-2 border-amber-500/60 px-2 py-1.5 rounded-sm">
              <span className="font-semibold text-amber-400 shrink-0">Late In:</span>
              <span className="text-foreground/90">{pair.clockIn.late_reason}</span>
            </div>
          )}
          {pair.clockOut?.late_reason && (
            <div className="flex items-start gap-2 text-xs bg-orange-500/10 border-l-2 border-orange-500/60 px-2 py-1.5 rounded-sm">
              <span className="font-semibold text-orange-400 shrink-0">Late Out:</span>
              <span className="text-foreground/90">{pair.clockOut.late_reason}</span>
            </div>
          )}
        </div>
      )}
      {/* Flags */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {pair.clockIn?.allocated_by && (
          <Badge className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-500 border-amber-500/40">Manual In</Badge>
        )}
        {pair.clockOut?.auto_clock_out && (
          <Badge className="text-[10px] uppercase tracking-wider bg-red-500/15 text-red-500 border-red-500/40">Auto Out</Badge>
        )}
        {(pair.clockIn?.edited_by_admin || pair.clockOut?.edited_by_admin) && (
          <Badge className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-500 border-blue-500/40">Edited</Badge>
        )}
        {(pair.clockIn?.latitude || pair.clockOut?.latitude) ? (
          <Badge className="text-[10px] uppercase tracking-wider bg-green-500/15 text-green-500 border-green-500/40">
            <MapPin className="w-3 h-3 mr-1" /> GPS
          </Badge>
        ) : pair.clockIn ? (
          <Badge className="text-[10px] uppercase tracking-wider bg-muted text-muted-foreground border-border">
            No GPS
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

function ClockSide({ kind, event, onEdit, onDelete, onViewPhoto, photoLoading }) {
  const isIn = kind === 'in';
  const color = isIn ? 'green' : 'red';
  return (
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-sm bg-${color}-400/10`}>
        {isIn ? <ArrowUpRight className={`w-3 h-3 text-${color}-400`} /> : <ArrowDownRight className={`w-3 h-3 text-${color}-400`} />}
      </div>
      <span className={`text-xs text-${color}-400 font-medium`}>{isIn ? 'IN' : 'OUT'}</span>
      {event ? (
        <>
          <span className="font-mono text-sm">{formatUK(event.timestamp, 'HH:mm')}</span>
          <div className="flex items-center gap-0.5 ml-auto">
            <Button variant="ghost" size="icon" onClick={() => onEdit(event)} className="h-7 w-7 hover:bg-primary/10" title="Edit">
              <Edit2 className="w-3 h-3 text-primary" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(event)} className="h-7 w-7 hover:bg-red-500/10" title="Delete">
              <Trash2 className="w-3 h-3 text-red-400" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onViewPhoto(event.id)} disabled={photoLoading} className="h-7 w-7" title="Photo">
              <ImageIcon className={`w-3 h-3 ${event.photo_url ? 'text-blue-400' : 'text-muted-foreground/40'}`} />
            </Button>
          </div>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">--:--</span>
      )}
    </div>
  );
}

function FeedbackRow({ feedback }) {
  const avg = (() => {
    const ratings = [feedback.uniform_rating, feedback.engagement_rating, feedback.information_rating, feedback.communication_rating, feedback.professionalism_rating].filter((r) => typeof r === 'number');
    if (!ratings.length) return 0;
    return (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);
  })();
  return (
    <div className="p-3 sm:p-4 hover:bg-accent/30">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {feedback.contact_name || 'Anonymous'} · <span className="text-muted-foreground">{feedback.card_code}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {(() => { try { return format(parseISO(feedback.created_at), 'MMM d, yyyy h:mm a'); } catch { return feedback.created_at; } })()}
          </p>
        </div>
        <Badge className="text-xs bg-amber-500/15 text-amber-500 border-amber-500/40">
          <Star className="w-3 h-3 mr-1" /> {avg}
        </Badge>
      </div>
      {feedback.comment && (
        <p className="text-xs text-foreground/80 mt-2 italic">&ldquo;{feedback.comment}&rdquo;</p>
      )}
    </div>
  );
}

function SwapRow({ swap, currentId }) {
  const isRequester = swap.requester_id === currentId;
  const statusMap = {
    awaiting_colleague: { label: 'Awaiting Colleague', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/40' },
    pending:            { label: 'Awaiting Admin',     cls: 'bg-blue-500/15 text-blue-500 border-blue-500/40' },
    approved:           { label: 'Approved',           cls: 'bg-green-500/15 text-green-500 border-green-500/40' },
    rejected:           { label: 'Rejected',           cls: 'bg-red-500/15 text-red-500 border-red-500/40' },
    cancelled:          { label: 'Cancelled',          cls: 'bg-slate-500/15 text-slate-400 border-slate-500/40' },
  };
  const s = statusMap[swap.status] || { label: swap.status, cls: 'bg-muted text-muted-foreground border-border' };
  return (
    <div className="p-3 sm:p-4 hover:bg-accent/30">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {isRequester ? 'Requested swap with' : 'Asked by'}{' '}
            <span className="text-muted-foreground">{isRequester ? swap.swap_with_name : swap.requester_name}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Shift: {swap.shift_date} {swap.shift_start_time && `· ${swap.shift_start_time}–${swap.shift_end_time}`}
          </p>
        </div>
        <Badge className={cn('text-[10px] uppercase tracking-wider border', s.cls)}>{s.label}</Badge>
      </div>
      {swap.rejection_reason && (
        <p className="text-xs text-red-400 mt-1 italic">{swap.rejection_reason}</p>
      )}
    </div>
  );
}

// ---------- Helpers ----------
function procedursesToList(list) { // tiny passthrough — kept for future extension
  return Array.isArray(list) ? list : [];
}

function pairClockEvents(history) {
  const all = [...history].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const pairs = [];
  const usedIds = new Set();
  const byShift = {};
  all.forEach((ev) => { if (ev.shift_id) { (byShift[ev.shift_id] ||= []).push(ev); } });
  Object.values(byShift).forEach((evs) => {
    const ins = evs.filter((e) => e.event_type === 'clock_in');
    const outs = evs.filter((e) => e.event_type === 'clock_out');
    ins.forEach((ci, i) => {
      const co = outs[i] || null;
      usedIds.add(ci.id); if (co) usedIds.add(co.id);
      pairs.push({ date: ci.timestamp.substring(0, 10), clockIn: ci, clockOut: co });
    });
    outs.slice(ins.length).forEach((co) => {
      if (usedIds.has(co.id)) return;
      usedIds.add(co.id);
      pairs.push({ date: co.timestamp.substring(0, 10), clockIn: null, clockOut: co });
    });
  });
  const remaining = all.filter((e) => !usedIds.has(e.id));
  let pendingIn = null;
  remaining.forEach((ev) => {
    if (ev.event_type === 'clock_in') {
      if (pendingIn) pairs.push({ date: pendingIn.timestamp.substring(0, 10), clockIn: pendingIn, clockOut: null });
      pendingIn = ev;
    } else if (pendingIn) {
      pairs.push({ date: pendingIn.timestamp.substring(0, 10), clockIn: pendingIn, clockOut: ev });
      pendingIn = null;
    } else {
      pairs.push({ date: ev.timestamp.substring(0, 10), clockIn: null, clockOut: ev });
    }
  });
  if (pendingIn) pairs.push({ date: pendingIn.timestamp.substring(0, 10), clockIn: pendingIn, clockOut: null });
  return pairs.sort((a, b) => {
    const tA = a.clockIn?.timestamp || a.clockOut?.timestamp || '';
    const tB = b.clockIn?.timestamp || b.clockOut?.timestamp || '';
    return tB.localeCompare(tA);
  });
}


// ----- Position inline editor -----
// Admin-only Position card row. Reads the canonical option list from
// /lib/positions.js (mirrored on the backend), sends a single-field PUT
// to /admin/employees/{id}/profile, and surfaces any backend validation
// error inline via toast. The grid cell is full-width on mobile so the
// dropdown gets enough room next to its label.
function PositionEditor({ employeeId, value, onSaved }) {
  const [saving, setSaving] = useState(false);
  const save = async (next) => {
    if (!next || next === value) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/employees/${employeeId}/profile`, { position: next });
      onSaved?.(res.data?.position || next);
      toast.success(`Position set to ${next}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update position');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="sm:col-span-2 flex items-start gap-3" data-testid="employee-position-row">
      <Briefcase className="w-4 h-4 mt-2 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Position</span>
          <CopyBtn value={value} label="Position" />
        </div>
        <Select value={value || ''} onValueChange={save} disabled={saving}>
          <SelectTrigger
            className="h-9 bg-background border-border rounded-sm max-w-xs"
            data-testid="employee-position-select"
          >
            <SelectValue placeholder="Not set — select position" />
          </SelectTrigger>
          <SelectContent>
            {POSITION_OPTIONS.map((p) => (
              <SelectItem key={p} value={p} data-testid={`employee-position-opt-${p.replace(/\W+/g, '-')}`}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ----- Application Details card (Admin → Employee Profile) -----
// Surfaces the fields harvested from the original Forms.app application AND
// lets the admin edit any of them. Saves via the same admin profile PUT
// already used for Position, so a single endpoint handles every field with
// proper allow-listing on the server.
const ADMIN_APP_FIELDS = [
  { key: 'first_name',                  label: 'First name' },
  { key: 'middle_name',                 label: 'Middle name' },
  { key: 'surname',                     label: 'Surname' },
  { key: 'gender',                      label: 'Gender' },
  { key: 'date_of_birth',               label: 'Date of birth',          type: 'date' },
  { key: 'sia_badge_number',            label: 'SIA Badge number' },
  { key: 'sia_badge_expiry_date',       label: 'SIA Badge expiry date',  type: 'date' },
  { key: 'address',                     label: 'Address',                wide: true },
  { key: 'city',                        label: 'City' },
  { key: 'postcode',                    label: 'Postcode' },
  { key: 'phone',                       label: 'Contact number' },
  { key: 'id_document_type',            label: 'ID Document type' },
  { key: 'id_document_number',          label: 'ID Document number' },
  { key: 'id_expiry_date',              label: 'ID expiry date',         type: 'date' },
  { key: 'id_issue_date',               label: 'ID issue date',          type: 'date' },
  { key: 'id_country_of_issue',         label: 'ID Country of issue' },
  { key: 'current_employment_status',   label: 'What is your current employment status?', wide: true },
  { key: 'bank_details',                label: 'Bank details',           wide: true },
  { key: 'emergency_contact_name',      label: 'EMERGENCY CONTACT NAME' },
  { key: 'emergency_contact_number',    label: 'EMERGENCY CONTACT NUMBER' },
];

function ApplicationDetailsCard({ employee, employeeId, onSaved }) {
  const [draft, setDraft] = useState(() => {
    const init = {};
    ADMIN_APP_FIELDS.forEach((f) => { init[f.key] = employee?.[f.key] || ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const next = {};
    ADMIN_APP_FIELDS.forEach((f) => { next[f.key] = employee?.[f.key] || ''; });
    setDraft(next);
    setDirty(false);
  }, [employee?.id]);

  const update = (k, v) => { setDraft((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      // Send only changed fields so the audit log stays meaningful.
      const payload = {};
      ADMIN_APP_FIELDS.forEach((f) => {
        const cur = employee?.[f.key] || '';
        if ((draft[f.key] || '') !== cur) payload[f.key] = draft[f.key];
      });
      if (Object.keys(payload).length === 0) { setDirty(false); return; }
      const res = await axios.put(`${API}/admin/employees/${employeeId}/profile`, payload);
      onSaved?.(res.data || {});
      toast.success('Application details updated');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="application-details-card">
      <CardHeader className="border-b border-border pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} /> Application Details
        </CardTitle>
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs"
          data-testid="save-application-details-btn"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </CardHeader>
      <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ADMIN_APP_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'sm:col-span-2 space-y-1' : 'space-y-1'}>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{f.label}</Label>
              <CopyBtn value={draft[f.key]} label={f.label} />
            </div>
            <Input
              type={f.type || 'text'}
              value={draft[f.key] || ''}
              onChange={(e) => update(f.key, e.target.value)}
              className="rounded-sm h-9"
              data-testid={`admin-field-${f.key}`}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


// ----- National Insurance card (main admin only, READ-ONLY) -----
// Shows the NI number the employee entered in My Profile (copy only — admin
// cannot edit it here) + the Proof of NI documents uploaded via Documents.
function NationalInsuranceCard({ employeeId }) {
  const [niNumber, setNiNumber] = useState('');
  const [proofDocs, setProofDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/employees/${employeeId}/profile`).catch(() => ({ data: {} })),
      axios.get(`${API}/admin/employees/${employeeId}/documents`).catch(() => ({ data: [] })),
    ]).then(([p, d]) => {
      if (cancelled) return;
      setNiNumber(p.data?.ni_number || '');
      setProofDocs((d.data || []).filter((doc) => doc.doc_type === 'proof_of_ni' && doc.is_current_version));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  const download = async (doc) => {
    try {
      const r = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch { toast.error('Failed to open document'); }
  };

  const statusBadge = (s) => {
    const map = {
      approved: 'bg-green-500/15 text-green-500 border-green-500/40',
      pending: 'bg-amber-500/15 text-amber-500 border-amber-500/40',
      rejected: 'bg-red-500/15 text-red-500 border-red-500/40',
    };
    return map[s] || 'bg-muted text-muted-foreground border-border';
  };

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="ni-card">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <Hash className="w-4 h-4 text-primary" strokeWidth={1.5} /> National Insurance
        </CardTitle>
        <p className="text-[11px] text-muted-foreground font-normal normal-case tracking-normal">
          Entered by the staff member in My Profile. Read-only — copy only. Proof is uploaded via Documents.
        </p>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground italic">Loading…</p>
        ) : (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">NI Number</p>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="font-mono text-base text-foreground" data-testid="ni-number-value">
                  {niNumber || '— not provided yet —'}
                </p>
                {niNumber && <CopyBtn value={niNumber} label="NI Number" />}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Proof of National Insurance Number</p>
              {proofDocs.length === 0 ? (
                <p className="text-xs text-muted-foreground italic" data-testid="ni-proof-empty">
                  No proof uploaded yet — the staff member can add it from Documents → Proof of National Insurance Number.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {proofDocs.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 bg-muted/30 border border-border/60 rounded-sm px-2 py-1.5" data-testid={`ni-proof-${doc.id}`}>
                      <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-xs text-foreground truncate flex-1">{doc.file_name}</span>
                      <Badge className={`text-[9px] uppercase tracking-wider border ${statusBadge(doc.status)}`}>{String(doc.status || '').replace(/_/g, ' ')}</Badge>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => download(doc)}
                        className="h-6 px-2 text-[10px] border-border rounded-sm"
                        data-testid={`ni-proof-view-${doc.id}`}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ----- Bank Details card (main admin only) -----
// Structured account holder / sort code / account number, backed by the
// same /admin/employees/{id}/bank endpoints used by the Payroll panel.
// Works for every staff role: employee, supervisor, secondary admin.
function BankDetailsCard({ employeeId }) {
  const [bank, setBank] = useState({ account_holder: '', sort_code: '', account_number: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/employees/${employeeId}/bank`)
      .then((r) => { if (!cancelled) setBank({
        account_holder: r.data.account_holder || '',
        sort_code: r.data.sort_code || '',
        account_number: r.data.account_number || '',
      }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  const update = (k, v) => { setBank((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/employees/${employeeId}/bank`, bank);
      toast.success('Bank details saved');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  const empty = !loading && !bank.account_holder && !bank.sort_code && !bank.account_number;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="bank-details-card">
      <CardHeader className="border-b border-border pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary" strokeWidth={1.5} /> Bank Details
        </CardTitle>
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs"
          data-testid="save-bank-details-btn"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {empty && (
          <p className="text-xs text-amber-400" data-testid="bank-details-empty-note">
            ⚠ No bank details on file yet — the staff member can add them from My Profile → Bank Details, or you can enter them here.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account Holder Name</Label>
              <CopyBtn value={bank.account_holder} label="Account Holder" />
            </div>
            <Input value={bank.account_holder} onChange={(e) => update('account_holder', e.target.value)}
                   disabled={loading} className="rounded-sm h-9" data-testid="admin-bank-account-holder" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sort Code</Label>
              <CopyBtn value={bank.sort_code} label="Sort Code" />
            </div>
            <Input value={bank.sort_code} onChange={(e) => update('sort_code', e.target.value)}
                   disabled={loading} placeholder="00-00-00" className="rounded-sm h-9" data-testid="admin-bank-sort-code" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account Number</Label>
              <CopyBtn value={bank.account_number} label="Account Number" />
            </div>
            <Input value={bank.account_number} onChange={(e) => update('account_number', e.target.value)}
                   disabled={loading} placeholder="12345678" className="rounded-sm h-9" data-testid="admin-bank-account-number" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Privacy: bank details are only visible to the main Admin and the staff member — supervisors cannot see them.
        </p>
      </CardContent>
    </Card>
  );
}
